import { BrowserPrintDeviceError } from "./errors";
import {
  type BrowserPrintApiOptions,
  normalizeErrorMessage,
  normalizePrinterDevice,
  resolveBrowserPrintApi,
  withTimeout,
} from "./internal";
import type { BrowserPrintPrinterDevice } from "./types";

export interface BrowserPrintDeviceOptions extends BrowserPrintApiOptions {}

export async function listPrinterDevices(
  options: BrowserPrintDeviceOptions = {},
): Promise<BrowserPrintPrinterDevice[]> {
  const api = resolveBrowserPrintApi(options);

  return await withTimeout<BrowserPrintPrinterDevice[]>(
    options.timeoutMs,
    "Timed out while listing BrowserPrint printer devices.",
    (resolve, reject) => {
      api.getLocalDevices(
        (devices) => {
          try {
            if (!Array.isArray(devices)) {
              reject(
                new BrowserPrintDeviceError(
                  "BrowserPrint returned non-array device list.",
                ),
              );
              return;
            }

            const normalized = devices.map((device, index) =>
              normalizePrinterDevice(device, `getLocalDevices()[${index}]`),
            );
            resolve(normalized);
          } catch (error) {
            reject(error);
          }
        },
        (error) =>
          reject(
            new BrowserPrintDeviceError(
              normalizeErrorMessage(
                error,
                "Failed to list BrowserPrint devices.",
              ),
              { cause: error },
            ),
          ),
        "printer",
      );
    },
  );
}

export async function getDefaultPrinterDevice(
  options: BrowserPrintDeviceOptions = {},
): Promise<BrowserPrintPrinterDevice> {
  const api = resolveBrowserPrintApi(options);

  return await withTimeout<BrowserPrintPrinterDevice>(
    options.timeoutMs,
    "Timed out while resolving BrowserPrint default printer.",
    (resolve, reject) => {
      api.getDefaultDevice(
        "printer",
        (device) => {
          try {
            resolve(normalizePrinterDevice(device, "getDefaultDevice()"));
          } catch (error) {
            reject(error);
          }
        },
        (error) =>
          reject(
            new BrowserPrintDeviceError(
              normalizeErrorMessage(
                error,
                "Failed to resolve BrowserPrint default printer.",
              ),
              { cause: error },
            ),
          ),
      );
    },
  );
}

export async function findPrinterDeviceByName(
  name: string,
  options: BrowserPrintDeviceOptions = {},
): Promise<BrowserPrintPrinterDevice | undefined> {
  const target = name.trim().toLowerCase();
  if (target.length === 0) {
    return undefined;
  }

  const devices = await listPrinterDevices(options);
  return devices.find((device) => device.name?.trim().toLowerCase() === target);
}
